import type { ReactElement } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Card, Stack, Alert } from "@swot";
import { useCmtsSelector } from "@store/hooks.js";
import { selectPermissionState, selectUiState } from "@store/selectors.js";

type AccessDeniedState = {
  readonly requiredPermission?: string | undefined;
  readonly requiredActionCode?: string | undefined;
  readonly attemptedPath?: string | undefined;
  readonly message?: string | undefined;
};

function readState(value: unknown): AccessDeniedState {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return {};
  const record = value as Record<string, unknown>;
  return {
    requiredPermission: typeof record.requiredPermission === "string" ? record.requiredPermission : undefined,
    requiredActionCode: typeof record.requiredActionCode === "string" ? record.requiredActionCode : undefined,
    attemptedPath: typeof record.attemptedPath === "string" ? record.attemptedPath : undefined,
    message: typeof record.message === "string" ? record.message : undefined,
  };
}

export function CmtsAccessDeniedPage(): ReactElement {
  const location = useLocation();
  const navigate = useNavigate();
  const state = readState(location.state);
  const lastBlock = useCmtsSelector((root) => selectPermissionState(root).lastPermissionBlock);
  const correlationId = useCmtsSelector((root) => selectUiState(root).lastCorrelationId);
  const requiredPermission = state.requiredPermission ?? lastBlock?.permission;

  const attemptedPath = state.attemptedPath ?? lastBlock?.route ?? location.pathname;
  const message = state.message ?? lastBlock?.message ?? "The active backend permission state does not allow this route.";
  return (
    <Card title="CMTS access denied">
      <Stack gap="md">
        <Alert tone="warning" title="Permission required">{message}</Alert>
        <div className="cmts-route-diagnostic-grid">
          <div><strong>Attempted route</strong><span>{attemptedPath}</span></div>
          <div><strong>Required permission</strong><span>{requiredPermission ?? "Not provided"}</span></div>
          <div><strong>Required action</strong><span>{state.requiredActionCode ?? "Not provided"}</span></div>
          <div><strong>Correlation ID</strong><span>{correlationId ?? lastBlock?.correlationId ?? "Not available"}</span></div>
        </div>
        <div className="cmts-route-actions">
          <Button intent="button" onClick={() => navigate(-1)}>Go back</Button>
          <Button intent="button" variant="primary" onClick={() => navigate("/cmts/my-workspace", { replace: true })}>Open my workspace</Button>
        </div>
      </Stack>
    </Card>
  );
}
